// src/api/users.ts
// Follows the same pattern as events.ts: one function per backend route,
// typed request params, typed response. Imports from ../types for shapes.

import { apiClient } from "./client";
import type {
  User, UserSummary, DashboardData, LeaderboardResult, LedgerEntry, ExecOffice, MemberStatus, UserRole, PointsReset
} from "../types";

export async function getMe(): Promise<User> {
  const { data } = await apiClient.get<{ user: User }>("/users/me");
  return data.user;
}

export async function getDashboard(): Promise<DashboardData> {
  const { data } = await apiClient.get<DashboardData>("/users/me/dashboard");
  return data;
}

export async function getRoster(params?: {
  q?: string;
  role?: string;
  status?: string;
  page?: number;
  limit?: number;
}): Promise<{ users: UserSummary[]; total: number }> {
  const { data } = await apiClient.get<{ users: UserSummary[]; total: number }>(
    "/users",
    { params }
  );
  return data;
}

export async function getMemberProfile(userId: string): Promise<User> {
  const { data } = await apiClient.get<{ user: User }>(`/users/${userId}`);
  return data.user;
}

/** Lightweight, name-only member search — any authenticated chapter member
 * can call this (unlike getRoster, which is Exec+ and returns email/role/
 * status). Backs the self-service Big/Little picker on ProfilePage.tsx: a
 * member choosing their own Big or a Little shouldn't need roster access or
 * see other members' emails to do it. */
export async function searchMembers(q: string): Promise<
  { id: string; firstName: string; lastName: string; avatarUrl?: string | null }[]
> {
  if (!q.trim()) return [];
  const { data } = await apiClient.get<{ users: { id: string; firstName: string; lastName: string; avatarUrl?: string | null }[] }>(
    "/users/search",
    { params: { q } }
  );
  return data.users;
}

export async function updateUserRole(
  userId: string,
  role: User["role"]
): Promise<User> {
  const { data } = await apiClient.patch<{ user: User }>(
    `/users/${userId}/role`,
    { role }
  );
  return data.user;
}

// Fuller editor (spec §4 — Super Admin: assign roles, assign exec offices,
// change membership status). role/office/status are independent fields —
// only the ones present in payload are touched.
export async function updateUserFields(
  userId: string,
  payload: {
    role?: UserRole;
    office?: ExecOffice | null;
    status?: MemberStatus;
    pledgeClassLabel?: string | null;
    major?: string | null;
    graduationYear?: number | null;
  }
): Promise<User> {
  const { data } = await apiClient.patch<{ user: User }>(`/users/${userId}`, payload);
  return data.user;
}

// Self-service profile edit (spec §9/§13 — closes the previous gap where no
// self-edit flow existed at all). Never accepts role/office/status/roleNumber.
export async function updateMyProfile(payload: {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  avatarUrl?: string | null;
  major?: string | null;
  graduationYear?: number | null;
}): Promise<User> {
  const { data } = await apiClient.patch<{ user: User }>("/users/me", payload);
  return data.user;
}

export async function getLeaderboard(params?: { semesterId?: string; resetId?: string }): Promise<LeaderboardResult> {
  const { data } = await apiClient.get("/points/leaderboard", { params });
  return data;
}

/** "Reset team and personal points... without altering attendance
 * tracking. This is different from the semester reset." — keeps the
 * current Semester exactly as it is; only tags PointsLedger rows so the
 * leaderboard reads 0 going forward. See listPointsResets() for the
 * resulting period's place in the history picker. */
export async function resetPoints(): Promise<PointsReset> {
  const { data } = await apiClient.post<{ reset: PointsReset }>("/points/reset");
  return data.reset;
}

export async function listPointsResets(semesterId?: string): Promise<PointsReset[]> {
  const { data } = await apiClient.get<{ resets: PointsReset[] }>("/points/resets", { params: { semesterId } });
  return data.resets;
}

export async function getPointsLedger(
  userId: string,
  params?: { semesterId?: string; limit?: number; cursor?: string }
): Promise<{ entries: LedgerEntry[]; total: number; nextCursor: string | null }> {
  const { data } = await apiClient.get(`/points/ledger/${userId}`, { params });
  return data;
}

export async function adjustPoints(payload: {
  userId: string;
  semesterId: string;
  amount: number;
  type: "BONUS" | "PENALTY" | "MANUAL_ADJUSTMENT";
  reason: string;
}): Promise<LedgerEntry> {
  const { data } = await apiClient.post<{ entry: LedgerEntry }>("/points/adjust", payload);
  return data.entry;
}

/** Deletes the caller's own account — removes the live Clerk account and
 * soft-deletes the local record in one call. Sign-in stops working
 * immediately; call useAppAuth().signOut() right after this resolves. */
export async function deleteMyAccount(): Promise<void> {
  await apiClient.delete("/users/me");
}

/** Super Admin only; deleting your own account through this path is
 * blocked server-side — use deleteMyAccount() for that. */
export async function deleteMemberAccount(userId: string): Promise<void> {
  await apiClient.delete(`/users/${userId}`);
}
